/**
 * Canonical names for one-shot *action* query parameters — params that trigger
 * a side effect exactly once at their destination and are then consumed, as
 * opposed to durable state (e.g. `env`, `region`) that should stay in the URL.
 */
export const APPLY_TEMPLATE_PARAM = 'applyTemplate';
export const OPEN_TEMPLATES_PARAM = 'openTemplates';
// Mirror of CREATE_FLAG_PARAM in routes/queryParams.ts. The `constants` layer
// cannot import from the higher `routes` layer, so the literal is duplicated
// here and a drift-guard test locks the two together.
const CREATE_FLAG_PARAM = 'create';

/**
 * Query parameters that are one-shot *actions*, not durable app state, and so
 * must never be preserved or restored across navigations.
 *
 * The preserved-navigation system deliberately keeps params sticky (e.g.
 * `?env=staging`) so they survive SPA navigation. That behaviour is wrong for
 * action params that fire once at their destination and are then consumed:
 * keeping them sticky makes the action re-fire on every later navigation.
 * Concretely, `applyTemplate` would re-apply a workflow template onto whichever
 * workflow the user opens next and (with instant autosave) overwrite its
 * definition — issue #2688. `openTemplates` re-opens the template gallery, and
 * `create` re-opens the create-workflow dialog, by the same mechanism.
 *
 * Consumed by `mergeSearch` (dropped from the current URL before merging) and
 * `QueryParamPreserver` (never restored by the safety net). Lives in the
 * `constants` layer so both the `utils` and `hooks`/`components` layers can
 * depend on it without violating the layered-architecture boundaries.
 */
export const NON_PRESERVED_QUERY_PARAMS = [
  APPLY_TEMPLATE_PARAM,
  OPEN_TEMPLATES_PARAM,
  CREATE_FLAG_PARAM,
] as const;
